import useAmplitude from '@eversports/amplitude-react/useAmplitude'
import ArrowsDownToPeopleIcon from '@eversports/design-tokens/assets/icons/regular/arrows-down-to-people.svg'
import CopyIcon from '@eversports/design-tokens/assets/icons/regular/copy.svg'
import CheckIcon from '@eversports/design-tokens/assets/icons/solid/check.svg'
import Button from '@eversports/klimt-primitives/Button'
import Box from '@eversports/klimt-primitives/legacy/Box'
import Heading from '@eversports/klimt-primitives/legacy/Heading'
import Icon from '@eversports/klimt-primitives/legacy/Icon'
import Stack from '@eversports/klimt-primitives/legacy/Stack'
import Svg from '@eversports/klimt-primitives/legacy/Svg'
import Text from '@eversports/klimt-primitives/legacy/Text'
import { useState } from 'react'

import { UserProfileRoutes } from '../../../../components/Header/UserMenu/UserMenu.constants'
import { Localized, useIntl } from '../../../../localization/react'

import ShareIcon from './assets/icons/arrow-up-from-square.svg'

const COPY_FEEDBACK_DURATION_MS = 1500

interface Props {
  venueName: string
  companyName: string
  referralCode: string
  rewardDescription: string
  shareText: string
  image: string
}

export function VenueReferAFriendOverlayContent({
  venueName,
  companyName,
  referralCode,
  rewardDescription,
  shareText,
  image,
}: Props) {
  const { amplitude } = useAmplitude()
  const intl = useIntl()
  const [isCopied, setIsCopied] = useState(false)

  const handleCopy = () => {
    amplitude?.logEvent('Clicked Share Refer Code', {
      method: 'copy',
      from: 'refer modal',
      company: { name: companyName },
    })
    void navigator.clipboard
      .writeText(referralCode)
      .then(() => {
        setIsCopied(true)
        setTimeout(() => setIsCopied(false), COPY_FEEDBACK_DURATION_MS)
      })
      .catch(() => setIsCopied(false))
  }

  const handleShare = () => {
    if (typeof navigator === 'undefined' || !navigator.share) return
    amplitude?.logEvent('Clicked Share Refer Code', {
      method: 'share',
      from: 'refer modal',
      company: { name: companyName },
    })
    void navigator
      .share({
        text: shareText,
      })
      .catch(() => {
        // user cancelled or share unavailable
      })
  }

  return (
    <Stack gap={6}>
      <Stack direction="row" alignItems="center" gap={2}>
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexShrink: 0 }}>
          <Icon src={ArrowsDownToPeopleIcon} size="small" color="midnight" />
        </Box>

        {/* The 22px font size does not exist in the variants */}
        <Heading is="h1" variant="large" sx={{ fontSize: 22 }}>
          <Localized id="refer-a-friend-venue-dialog-title" params={{ venueName }} />
        </Heading>
      </Stack>

      <Box
        component="img"
        src={image}
        alt=""
        sx={{
          width: '100%',
          objectFit: 'cover',
          borderRadius: 1,
        }}
      />
      {rewardDescription && (
        <Text variant="large" sx={{ whiteSpace: 'pre-line', color: 'darkgray' }}>
          {rewardDescription}
        </Text>
      )}

      <Stack
        gap={{ xs: 4, sm: 6 }}
        sx={{
          position: { xs: 'sticky', sm: 'static' },
          backgroundColor: 'white',
          bottom: 0,
          borderTop: 1,
          borderColor: 'var(--color-border-divider)',
          paddingTop: 6,
          paddingBottom: { xs: 4, sm: 0 },
        }}
      >
        <Stack gap={1}>
          <Text variant="regular" sx={{ fontWeight: 'bold' }}>
            <Localized id="refer-a-friend-venue-dialog-code-label-at-venue" />
          </Text>
          <Stack direction="row" gap={2}>
            <Button variant="tertiary" size="sm" width="full" iconPosition="right" onClick={handleCopy}>
              <Text variant="regular" sx={{ fontWeight: 'medium', color: 'darkgray' }} className="grow">
                {referralCode}
              </Text>
              <Svg
                src={isCopied ? CheckIcon : CopyIcon}
                width={12}
                height={12}
                color={isCopied ? 'brightgreen' : 'darkgray'}
              />
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleShare}
              aria-label={intl.referAFriendVenueDialogShareButton()}
            >
              <Svg src={ShareIcon} width={12} height={12} color="darkgray" />
            </Button>
          </Stack>
        </Stack>
        <Button
          variant="primary"
          onClick={() => {
            window.location.href = `${UserProfileRoutes.MY_REFER_A_FRIEND}?from=venue-profile`
          }}
        >
          <Localized id="refer-a-friend-venue-dialog-hub-button" />
        </Button>
      </Stack>
    </Stack>
  )
}
